<script setup lang="ts">
import { ref } from 'vue'
import SetRealNameDialog from '@/components/business/me/SetRealNameDialog.vue'
import BindPhoneDialog from '@/components/business/account/BindPhoneDialog.vue'

defineProps({
  serviceName: {
    type: String,
    default: ''
  }
})

const reportDialogVisible = defineModel<boolean>('reportDialogVisible', { default: false })
const claimDialogVisible = defineModel<boolean>('claimDialogVisible', { default: false })
const bindPhoneDialogVisible = defineModel<boolean>('bindPhoneDialogVisible', { default: false })
const reportForm = defineModel<any>('reportForm', { default: () => ({ reason: '', content: '' }) })
const claimForm = defineModel<any>('claimForm', {
  default: () => ({
    relationship: '',
    identityProof: '',
    contactType: 'wechat',
    contactValue: ''
  })
})

const emit = defineEmits(['submitReport', 'submitClaim', 'claimImageChange'])

const realNameModalRef = ref<InstanceType<typeof SetRealNameDialog> | null>(null)

async function openRealNameDialog() {
  return await realNameModalRef.value?.open()
}

defineExpose({ openRealNameDialog })
</script>

<template>
  <ClientOnly>
    <div>
      <SetRealNameDialog ref="realNameModalRef" />
      <BindPhoneDialog :visible="bindPhoneDialogVisible" @finish="bindPhoneDialogVisible = false" />
    </div>
  </ClientOnly>

  <el-dialog
    v-model="reportDialogVisible"
    width="90%"
    max-width="400px"
    :show-close="true"
    center
    style="border-radius: 12px"
    class="custom-dialog">
    <template #header>
      <div class="text-lg font-bold text-center">举报纪念馆</div>
    </template>
    <div class="flex flex-col gap-3 px-2">
      <el-radio-group v-model="reportForm.reason" class="flex flex-col items-start gap-3 w-full">
        <el-radio label="侵犯个人权益（肖像、名誉、隐私等）" class="!mr-0 !mb-0 !h-auto"
          >侵犯个人权益（肖像、名誉、隐私等）</el-radio
        >
        <el-radio label="色情低俗" class="!mr-0 !mb-0 !h-auto">色情低俗</el-radio>
        <el-radio label="政治敏感/违法信息" class="!mr-0 !mb-0 !h-auto">政治敏感/违法信息</el-radio>
        <el-radio label="虚假信息/谣言" class="!mr-0 !mb-0 !h-auto">虚假信息/谣言</el-radio>
        <el-radio label="假冒/仿冒公众人物" class="!mr-0 !mb-0 !h-auto">假冒/仿冒公众人物</el-radio>
        <el-radio label="网络暴力/人身攻击" class="!mr-0 !mb-0 !h-auto">网络暴力/人身攻击</el-radio>
        <el-radio label="违规营销/恶意引流" class="!mr-0 !mb-0 !h-auto">违规营销/恶意引流</el-radio>
        <el-radio label="其他违法违规" class="!mr-0 !mb-0 !h-auto">其他违法违规</el-radio>
      </el-radio-group>
      <div class="mt-4">
        <div class="text-sm mb-2">具体举报内容及补充信息：<span class="text-red-500">*</span></div>
        <textarea
          v-model="reportForm.content"
          rows="4"
          class="w-full bg-gray-100 border-none rounded-lg p-3 outline-none resize-none text-sm"></textarea>
      </div>

      <div class="mt-6 flex justify-center">
        <button
          class="w-full bg-black text-white rounded-lg py-3 text-base font-medium active:opacity-80 transition-opacity"
          @click="$emit('submitReport')">
          举报纪念馆
        </button>
      </div>
    </div>
  </el-dialog>

  <el-dialog
    v-model="claimDialogVisible"
    width="90%"
    max-width="400px"
    :show-close="true"
    center
    style="border-radius: 12px"
    class="custom-dialog">
    <template #header>
      <div class="text-lg font-bold text-center">认领纪念馆</div>
    </template>
    <div class="flex flex-col gap-5 px-2">
      <div>
        <div class="text-sm font-bold mb-3">您是"{{ serviceName }}"的？</div>
        <el-radio-group v-model="claimForm.relationship" class="grid grid-cols-3 gap-y-3 gap-x-2 w-full">
          <el-radio label="家人" class="!mr-0 !mb-0 !h-auto">家人</el-radio>
          <el-radio label="朋友" class="!mr-0 !mb-0 !h-auto">朋友</el-radio>
          <el-radio label="单位/公司负责人" class="!mr-0 !mb-0 !h-auto">单位/公司负责人</el-radio>
          <el-radio label="经纪公司" class="!mr-0 !mb-0 !h-auto">经纪公司</el-radio>
          <el-radio label="代理人" class="!mr-0 !mb-0 !h-auto">代理人</el-radio>
          <el-radio label="粉丝组织" class="!mr-0 !mb-0 !h-auto">粉丝组织</el-radio>
        </el-radio-group>
      </div>

      <div>
        <div class="text-sm font-bold mb-3">您或组织的身份证明：</div>
        <el-upload
          class="avatar-uploader"
          action=""
          :show-file-list="false"
          :auto-upload="false"
          :on-change="(f) => $emit('claimImageChange', f)">
          <div
            v-if="claimForm.identityProof"
            class="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
            <img :src="claimForm.identityProof" class="w-full h-full object-cover" />
          </div>
          <div
            v-else
            class="w-24 h-24 bg-gray-100 flex items-center justify-center rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
            <div class="i-ep-plus text-3xl text-gray-400"></div>
          </div>
        </el-upload>
      </div>

      <div>
        <div class="text-sm font-bold mb-3">您的联系方式</div>
        <el-radio-group v-model="claimForm.contactType" class="flex gap-6 mb-3">
          <el-radio label="wechat" class="!mr-0 !mb-0 !h-auto">微信号</el-radio>
          <el-radio label="phone" class="!mr-0 !mb-0 !h-auto">电话号码</el-radio>
        </el-radio-group>
        <input
          v-model="claimForm.contactValue"
          type="text"
          class="w-full bg-gray-100 border-none rounded-lg h-10 px-3 outline-none text-sm" />
      </div>

      <div class="mt-4 flex justify-center">
        <button
          class="w-full bg-black text-white rounded-lg py-3 text-base font-medium active:opacity-80 transition-opacity"
          @click="$emit('submitClaim')">
          我要认领纪念馆
        </button>
      </div>
    </div>
  </el-dialog>
</template>
